import { juvareColors, themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { Box } from '@mui/material';
import React from 'react';
import { FormConfigContext } from '../FormConfigContext';
import { icons } from '../icons/icons';
import { HtmlOutput } from './index';
/**
 * A helper component that demonstrates how custom HTML examples render using the `HtmlOutput` component.
 * It retrieves a list of HTML snippets using `getExamples`, and for each, it shows the raw HTML and the rendered result.
 * Uses `FormConfigContext` to access optional custom icon configuration for the examples.
 *
 * @component
 *
 * @returns {React.ReactElement} A rendered list of example HTML outputs with source and visual output.
 */
export const HtmlOutputHelp = () => {
    var _a;
    const context = React.useContext(FormConfigContext);
    const customIcons = (_a = context.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.htmlOutputIcons;
    return (React.createElement(Box, null, getExamples(customIcons).map((example) => (React.createElement(Box, { key: example, pt: 1, pb: 1, sx: { borderBottom: (theme) => '1px solid ' + theme.palette.divider } },
        React.createElement("pre", { style: { margin: 0, marginBottom: 8 } }, example),
        React.createElement(HtmlOutput, { html: example }))))));
};
function getExamples(customIcons) {
    return [
        '<p class="subtitle1">Paragraph Sub-Title 1</p>',
        '<p class="subtitle2">Paragraph Sub-Title 2</p>',
        '<p class="body1">Paragraph Body 1</p>',
        '<p class="body2">Paragraph Body 2</p>',
        '<p class="caption">Paragraph Caption</p>',
        '<p class="button">Paragraph Button</p>',
        '<p class="overline">Paragraph Overline</p>',
        '<h1>Heading Level 1</h1>',
        '<h2>Heading Level 2</h2>',
        '<h3>Heading Level 3</h3>',
        '<h4>Heading Level 4</h4>',
        '<h5>Heading Level 5</h5>',
        '<h6>Heading Level 6</h6>',
        '<div style="border: 1px solid #555; border-radius: 12px; padding: 8px; background: rgba(0, 0, 0, 0.05);">\n  Some Generic Content\n</div>',
        '<ul>\n  <li>Item A</li>\n  <li>Item B</li>\n  <li>Item C</li>\n</ul>',
        '<ol>\n  <li>Item A</li>\n  <li>Item B</li>\n  <li>Item C</li>\n</ol>',
        '<p>Some <span style="text-decoration: line-through;">Inline</span> Content</p>',
        '<p>Link to <a href="https://juvare.com/">Juvare</a></p>',
        '<img src="https://www.juvare.com/wp-content/uploads/2023/11/Juvare-Logo-Red-Shield-with-White-Text-Horizontal.svg" alt="juvare logo">',
        '<p class="subtitle1">All Icons</p><p class="body2">' + buildIcons(customIcons) + '\n</p>',
        '<p class="subtitle1">All Colors By Class</p>\n' +
            '<p class="body2 primary">Color primary</p>\n' +
            '<p class="body2 primary-light">Color primary-light</p>\n' +
            '<p class="body2 primary-dark">Color primary-dark</p>\n' +
            '<p class="body2 secondary">Color secondary</p>\n' +
            '<p class="body2 secondary-light">Color secondary-light</p>\n' +
            '<p class="body2 secondary-dark">Color secondary-dark</p>\n' +
            '<p class="body2 error">Color error</p>\n' +
            '<p class="body2 error-light">Color error-light</p>\n' +
            '<p class="body2 error-dark">Color error-dark</p>\n' +
            '<p class="body2 warning">Color warning</p>\n' +
            '<p class="body2 warning-light">Color warning-light</p>\n' +
            '<p class="body2 warning-dark">Color warning-dark</p>\n' +
            '<p class="body2 info">Color info</p>\n' +
            '<p class="body2 info-light">Color info-light</p>\n' +
            '<p class="body2 info-dark">Color info-dark</p>\n' +
            '<p class="body2 success">Color success</p>\n' +
            '<p class="body2 success-light">Color success-light</p>\n' +
            '<p class="body2 success-dark">Color success-dark</p>\n' +
            '<p class="body2 grey-50">Color grey-50</p>\n' +
            '<p class="body2 grey-100">Color grey-100</p>\n' +
            '<p class="body2 grey-200">Color grey-200</p>\n' +
            '<p class="body2 grey-300">Color grey-300</p>\n' +
            '<p class="body2 grey-400">Color grey-400</p>\n' +
            '<p class="body2 grey-500">Color grey-500</p>\n' +
            '<p class="body2 grey-600">Color grey-600</p>\n' +
            '<p class="body2 grey-700">Color grey-700</p>\n' +
            '<p class="body2 grey-800">Color grey-800</p>\n' +
            '<p class="body2 grey-900">Color grey-900</p>\n' +
            '<p class="body2 grey-A100">Color grey-A100</p>\n' +
            '<p class="body2 grey-A200">Color grey-A200</p>\n' +
            '<p class="body2 grey-A400">Color grey-A400</p>\n' +
            '<p class="body2 grey-A700">Color grey-A700</p>\n' +
            '<p class="body2 text-primary">Color text-primary</p>\n' +
            '<p class="body2 text-secondary">Color text-secondary</p>\n' +
            '<p class="body2 text-disabled">Color text-disabled</p>' +
            buildThemeColors() +
            buildJuvareColors(),
    ];
}
function buildIcons(customIcons) {
    let html = '';
    for (const iconClassName of Object.keys(customIcons !== null && customIcons !== void 0 ? customIcons : icons)) {
        html += `\n<span class="${iconClassName}"></span>`;
    }
    return html;
}
function buildThemeColors() {
    let html = '\n<p class="subtitle1">Theme Classes - they work in dark mode</p>';
    walkThemeColors(themeColors, [], (colorNames, colorValue) => {
        const className = `body2 theme-${colorNames.join('-')}`;
        html += `\n<p class="${className}">Color ${className} ${colorValue}</p>`;
    });
    return html;
}
function walkThemeColors(colors, prefix, callback) {
    for (const [name, value] of Object.entries(colors)) {
        if (typeof value === 'string') {
            callback([...prefix, name], value);
        }
        else if (value != null) {
            walkThemeColors(value, [...prefix, name], callback);
        }
    }
}
function buildJuvareColors() {
    let html = '\n<p class="subtitle1">Deprecated Classes - they do not work in dark mode</p>';
    for (const [color, subColors] of Object.entries(juvareColors)) {
        for (const subColor of Object.keys(subColors)) {
            const className = `body2 juvare-${color}-${subColor}`;
            html += `\n<p class="${className}">Color ${className}</p>`;
        }
    }
    return html;
}
